import React, { useState } from "react";

const PRIORITY_LABELS = {
  low: "Низкий",
  medium: "Средний",
  high: "Высокий",
};

function formatDueDate(dueDate) {
  if (!dueDate) return null;
  const date = new Date(dueDate + "T00:00:00");
  return date.toLocaleDateString("ru-RU", { day: "numeric", month: "short" });
}

function isOverdue(dueDate) {
  if (!dueDate) return false;
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  const due = new Date(dueDate + "T00:00:00");
  return due < today;
}

function getInitials(name) {
  return name
    .split(/\s+/)
    .filter(Boolean)
    .slice(0, 2)
    .map((part) => part[0])
    .join("")
    .toUpperCase();
}

function WatchIcon() {
  return (
    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" aria-hidden="true">
      <path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z" />
      <circle cx="12" cy="12" r="3" />
    </svg>
  );
}

function taskAssignees(task) {
  if (Array.isArray(task.assignees) && task.assignees.length > 0) {
    return task.assignees;
  }
  return task.assignee ? [task.assignee] : [];
}

function TaskAssignees({ task }) {
  const assignees = taskAssignees(task);
  if (assignees.length === 0) {
    return (
      <div className="task-assignee-group">
        <span className="task-avatar task-avatar-empty" title="Не назначен" aria-hidden="true">
          ?
        </span>
        <span className="task-assignee task-assignee-empty">Не назначен</span>
      </div>
    );
  }
  const shown = assignees.slice(0, 3);
  const extra = assignees.length - shown.length;
  const names = assignees.map((person) => person.full_name).join(", ");
  return (
    <div className="task-assignee-group" title={names}>
      <span className="task-avatar-stack">
        {shown.map((person) => (
          <span key={person.id} className="task-avatar" title={person.full_name}>
            {getInitials(person.full_name)}
          </span>
        ))}
        {extra > 0 && (
          <span className="task-avatar task-avatar-more" title={names}>
            +{extra}
          </span>
        )}
      </span>
      <span className="task-assignee">
        {assignees.length === 1 ? assignees[0].full_name : `${assignees.length} исполн.`}
      </span>
    </div>
  );
}

function DragHandleIcon() {
  return (
    <svg width="12" height="16" viewBox="0 0 12 16" fill="currentColor" aria-hidden="true">
      <circle cx="3" cy="3" r="1.4" />
      <circle cx="9" cy="3" r="1.4" />
      <circle cx="3" cy="8" r="1.4" />
      <circle cx="9" cy="8" r="1.4" />
      <circle cx="3" cy="13" r="1.4" />
      <circle cx="9" cy="13" r="1.4" />
    </svg>
  );
}

export default function TaskCard({
  task,
  onClick,
  dragHandleProps,
  isDragging,
  filterHighlight = false,
  subtasks = [],
  onToggleSubtask,
}) {
  const [expanded, setExpanded] = useState(false);
  const overdue = !task.is_done && isOverdue(task.due_date);
  const hasSubtasks = task.subtask_count > 0;
  const allSubtasksDone = hasSubtasks && task.subtask_done_count === task.subtask_count;
  const progress = hasSubtasks
    ? Math.round((task.subtask_done_count / task.subtask_count) * 100)
    : 0;
  const canExpand = hasSubtasks && subtasks.length > 0;
  const showHandle = Boolean(dragHandleProps);

  const stop = (e) => e.stopPropagation();

  const handleToggleExpand = (e) => {
    e.stopPropagation();
    setExpanded((v) => !v);
  };

  const handleSubtaskToggle = (e, sub) => {
    e.stopPropagation();
    if (onToggleSubtask) onToggleSubtask(sub);
  };

  return (
    <article
      className={[
        "task-card",
        `task-card-priority-${task.priority}`,
        isDragging ? "task-card-dragging" : "",
        filterHighlight ? "task-card-filter-match" : "",
        allSubtasksDone ? "task-card-all-done" : "",
        showHandle ? "task-card-with-handle" : "",
      ]
        .filter(Boolean)
        .join(" ")}
      onClick={onClick}
    >
      {showHandle && (
        <button
          type="button"
          className="task-drag-handle"
          aria-label="Перетащить задачу"
          title="Перетащить"
          onClick={stop}
          {...dragHandleProps}
        >
          <DragHandleIcon />
        </button>
      )}

      <div className="task-card-body">
        <div className="task-card-top">
          <span className={`task-priority task-priority-${task.priority}`}>
            {PRIORITY_LABELS[task.priority] || task.priority}
          </span>
          {task.due_date && (
            <span
              className={`task-due-chip ${overdue ? "task-due-overdue" : ""}`}
              aria-label={overdue ? `Просрочено, ${formatDueDate(task.due_date)}` : formatDueDate(task.due_date)}
            >
              {overdue ? "!" : ""}
              {formatDueDate(task.due_date)}
            </span>
          )}
        </div>

        <h4 className="task-card-title">{task.title}</h4>

        {hasSubtasks && (
          <div
            className={`task-progress-block ${canExpand ? "task-progress-block-clickable" : ""}`}
            onClick={canExpand ? handleToggleExpand : undefined}
            onPointerDown={canExpand ? stop : undefined}
            role={canExpand ? "button" : undefined}
            tabIndex={canExpand ? 0 : undefined}
            aria-expanded={canExpand ? expanded : undefined}
            onKeyDown={
              canExpand
                ? (e) => {
                    if (e.key === "Enter" || e.key === " ") {
                      e.preventDefault();
                      handleToggleExpand(e);
                    }
                  }
                : undefined
            }
            title={canExpand ? (expanded ? "Свернуть подзадачи" : "Развернуть подзадачи") : undefined}
          >
            {canExpand && (
              <span
                className={`task-progress-caret ${expanded ? "task-progress-caret-open" : ""}`}
                aria-hidden="true"
              >
                ›
              </span>
            )}
            <div className="task-progress-bar">
              <div
                className={`task-progress-fill ${allSubtasksDone ? "task-progress-fill-done" : ""}`}
                style={{ width: `${progress}%` }}
              />
            </div>
            <span className="task-progress-label">
              {task.subtask_done_count}/{task.subtask_count}
            </span>
            {canExpand && (
              <span className="task-progress-toggle-text">
                {expanded ? "Скрыть" : "Подзадачи"}
              </span>
            )}
          </div>
        )}

        {canExpand && expanded && (
          <ul
            className="task-card-subtasks"
            onPointerDown={stop}
            onClick={stop}
          >
            {subtasks.map((sub) => (
              <li
                key={sub.id}
                className={`task-card-subtask ${sub.is_done ? "task-card-subtask-done" : ""}`}
              >
                <input
                  type="checkbox"
                  className="task-card-subtask-checkbox"
                  checked={Boolean(sub.is_done)}
                  onChange={(e) => handleSubtaskToggle(e, sub)}
                  onClick={stop}
                />
                <span className="task-card-subtask-title">{sub.title}</span>
              </li>
            ))}
          </ul>
        )}

        <div className="task-card-footer">
          <TaskAssignees task={task} />
          {Array.isArray(task.watchers) && task.watchers.length > 0 && (
            <span
              className="task-watchers-badge"
              title={task.watchers.map((person) => person.full_name).join(", ")}
            >
              <WatchIcon />
              {task.watchers.length}
            </span>
          )}
        </div>
      </div>
    </article>
  );
}
